import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * All filter state lives in URL search params (bookmarkable, shareable).
 *
 * URL param mapping:
 * - ticketType: comma-separated values
 * - category: comma-separated classification values, scoped to the pinned ticket type
 * - addrSource / addrAction: comma-separated ADDRESS_MANAGEMENT classification values
 * - status: comma-separated values
 * - priority: comma-separated values
 * - operatorId: single UUID
 * - assignedTo: single UUID
 * - slaStatus: single value (breached|atRisk|onTrack|none)
 * - createdAtFrom: ISO date string
 * - createdAtTo: ISO date string
 * - q: free-text search
 * - sort: sort field
 * - order: asc|desc
 *
 * Legacy backward compat:
 * - escalated=true → kept as-is
 * - slaBreached=true → mapped to slaStatus=breached
 */

export interface TicketFilters {
  ticketType: string[];
  /** Order subcategory / support category / build category, per the pinned ticket type. */
  category: string[];
  /** ADDRESS_MANAGEMENT facets, ANDed with each other server-side. */
  addrSource: string[];
  addrAction: string[];
  status: string[];
  priority: string[];
  operatorId: string | null;
  assignedTo: string | null;
  mentionedUserId: string | null;
  createdBy: string | null;
  groupId: string | null;
  slaStatus: string | null;
  createdAtFrom: string | null;
  createdAtTo: string | null;
  q: string;
  sort: string | null;
  order: 'asc' | 'desc' | null;
  escalated: boolean;
}

function parseArrayParam(params: URLSearchParams, key: string): string[] {
  const val = params.get(key);
  if (!val) return [];
  return val.split(',').filter(Boolean);
}

export function useTicketFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: TicketFilters = useMemo(() => {
    // Backward compat: slaBreached=true → slaStatus=breached
    const slaBreached = searchParams.get('slaBreached');
    const slaStatusRaw = searchParams.get('slaStatus');
    const slaStatus = slaStatusRaw ?? (slaBreached === 'true' ? 'breached' : null);

    return {
      ticketType: parseArrayParam(searchParams, 'ticketType'),
      category: parseArrayParam(searchParams, 'category'),
      addrSource: parseArrayParam(searchParams, 'addrSource'),
      addrAction: parseArrayParam(searchParams, 'addrAction'),
      status: parseArrayParam(searchParams, 'status'),
      priority: parseArrayParam(searchParams, 'priority'),
      operatorId: searchParams.get('operatorId'),
      assignedTo: searchParams.get('assignedTo'),
      mentionedUserId: searchParams.get('mentionedUserId'),
      createdBy: searchParams.get('createdBy'),
      groupId: searchParams.get('groupId'),
      slaStatus,
      createdAtFrom: searchParams.get('createdAtFrom'),
      createdAtTo: searchParams.get('createdAtTo'),
      q: searchParams.get('q') || '',
      sort: searchParams.get('sort'),
      order: (searchParams.get('order') as 'asc' | 'desc') || null,
      escalated: searchParams.get('escalated') === 'true',
    };
  }, [searchParams]);

  const setFilter = useCallback(
    // `opts.replace` swaps the history entry instead of pushing one. Used by self-correcting
    // updates (the ticket list prunes category values that do not belong to the pinned type),
    // which must not pollute the back button with URLs the user never chose.
    (key: string, value: string | string[] | null, opts?: { replace?: boolean }) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
          next.delete(key);
          // Clean up legacy params when using new equivalents
          if (key === 'slaStatus') next.delete('slaBreached');
        } else if (Array.isArray(value)) {
          next.set(key, value.join(','));
        } else {
          next.set(key, value);
        }
        return next;
      }, opts?.replace ? { replace: true } : undefined);
    },
    [setSearchParams],
  );

  /** Batch-set multiple filters in a single URL update */
  const setFilters = useCallback(
    (updates: Record<string, string | string[] | null>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
            next.delete(key);
            if (key === 'slaStatus') next.delete('slaBreached');
          } else if (Array.isArray(value)) {
            next.set(key, value.join(','));
          } else {
            next.set(key, value);
          }
        }
        return next;
      });
    },
    [setSearchParams],
  );

  const clearAll = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  const toApiParams = useCallback((): Record<string, string> => {
    const params: Record<string, string> = { limit: '100' };
    if (filters.ticketType.length) params.ticketType = filters.ticketType.join(',');
    if (filters.category.length) params.category = filters.category.join(',');
    if (filters.addrSource.length) params.addrSource = filters.addrSource.join(',');
    if (filters.addrAction.length) params.addrAction = filters.addrAction.join(',');
    if (filters.status.length) params.status = filters.status.join(',');
    if (filters.priority.length) params.priority = filters.priority.join(',');
    if (filters.operatorId) params.operatorId = filters.operatorId;
    if (filters.assignedTo) params.assignedTo = filters.assignedTo;
    if (filters.mentionedUserId) params.mentionedUserId = filters.mentionedUserId;
    if (filters.createdBy) params.createdBy = filters.createdBy;
    if (filters.groupId) params.groupId = filters.groupId;
    if (filters.slaStatus) params.slaStatus = filters.slaStatus;
    if (filters.createdAtFrom) params.createdAtFrom = filters.createdAtFrom;
    if (filters.createdAtTo) params.createdAtTo = filters.createdAtTo;
    if (filters.q) params.q = filters.q;
    if (filters.sort) params.sort = filters.sort;
    if (filters.order) params.order = filters.order;
    if (filters.escalated) params.escalated = 'true';
    return params;
  }, [filters]);

  // Count active filters (excluding sort/order/search which aren't "filter chips")
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.category.length) count++;
    if (filters.addrSource.length) count++;
    if (filters.addrAction.length) count++;
    if (filters.status.length) count++;
    if (filters.priority.length) count++;
    if (filters.operatorId) count++;
    if (filters.assignedTo) count++;
    if (filters.mentionedUserId) count++;
    if (filters.createdBy) count++;
    if (filters.groupId) count++;
    if (filters.slaStatus) count++;
    if (filters.createdAtFrom || filters.createdAtTo) count++;
    if (filters.escalated) count++;
    return count;
  }, [filters]);

  /** Apply a saved filter config to URL params */
  const applyFilterConfig = useCallback(
    (config: Record<string, unknown>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams();
        // Preserve sort/order if set
        const sort = prev.get('sort');
        const order = prev.get('order');
        if (sort) next.set('sort', sort);
        if (order) next.set('order', order);

        for (const [key, value] of Object.entries(config)) {
          if (value === null || value === undefined || value === '') continue;
          if (Array.isArray(value) && value.length === 0) continue;
          if (Array.isArray(value)) {
            next.set(key, value.join(','));
          } else {
            next.set(key, String(value));
          }
        }
        return next;
      });
    },
    [setSearchParams],
  );

  /** Export current filters as a config object (for saving) */
  const toFilterConfig = useCallback((): Record<string, unknown> => {
    const config: Record<string, unknown> = {};
    if (filters.ticketType.length) config.ticketType = filters.ticketType;
    if (filters.category.length) config.category = filters.category;
    if (filters.addrSource.length) config.addrSource = filters.addrSource;
    if (filters.addrAction.length) config.addrAction = filters.addrAction;
    if (filters.status.length) config.status = filters.status;
    if (filters.priority.length) config.priority = filters.priority;
    if (filters.operatorId) config.operatorId = filters.operatorId;
    if (filters.assignedTo) config.assignedTo = filters.assignedTo;
    if (filters.mentionedUserId) config.mentionedUserId = filters.mentionedUserId;
    if (filters.createdBy) config.createdBy = filters.createdBy;
    if (filters.groupId) config.groupId = filters.groupId;
    if (filters.slaStatus) config.slaStatus = filters.slaStatus;
    if (filters.createdAtFrom) config.createdAtFrom = filters.createdAtFrom;
    if (filters.createdAtTo) config.createdAtTo = filters.createdAtTo;
    if (filters.q) config.q = filters.q;
    if (filters.escalated) config.escalated = true;
    return config;
  }, [filters]);

  return {
    filters,
    setFilter,
    setFilters,
    clearAll,
    toApiParams,
    toFilterConfig,
    applyFilterConfig,
    activeFilterCount,
  };
}
